
import { useState, useEffect } from "react";
import { Check, X } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { usStates } from "@/utils/states";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/components/ui/use-toast";

const MarketsTab = () => {
  const { markets, updateMarkets, profile, updateProfile } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [userMarkets, setUserMarkets] = useState<{ id: string, name: string }[]>([]);
  const [marketSearch, setMarketSearch] = useState("");
  const [hideGrossMargin, setHideGrossMargin] = useState<boolean>(false);

  // Update local state when markets data changes
  useEffect(() => {
    if (markets) {
      const marketDetails = markets.map(market => {
        const state = usStates.find(s => s.id === market.state_id);
        return {
          id: market.state_id,
          name: state?.name || market.state_id
        };
      });
      setUserMarkets(marketDetails);
    }
  }, [markets]);

  useEffect(() => {
    setHideGrossMargin(!!profile?.hide_gross_margin);
  }, [profile?.hide_gross_margin]);

  // Filter available states based on search and exclude already selected
  const filteredStates = usStates
    .filter(state => 
      state.name.toLowerCase().includes(marketSearch.toLowerCase()) && 
      !userMarkets.some(m => m.id === state.id)
    );

  const addMarket = (state: typeof usStates[0]) => {
    if (!userMarkets.some(m => m.id === state.id)) {
      setUserMarkets([...userMarkets, { id: state.id, name: state.name }]);
    }
    setMarketSearch("");
  };

  const removeMarket = (stateId: string) => {
    setUserMarkets(userMarkets.filter(market => market.id !== stateId));
  };

  const handleSaveMarkets = async () => {
    setIsLoading(true);
    try {
      await updateMarkets(userMarkets.map(m => m.id));
      if (hideGrossMargin !== !!profile?.hide_gross_margin) {
        await updateProfile({ hide_gross_margin: hideGrossMargin } as any);
      }
      toast({
        title: "Settings saved",
        description: "Your markets and display preferences have been updated."
      });
    } catch (error) {
      console.error("Error updating markets:", error);
      toast({
        title: "Update failed",
        description: "There was a problem updating your settings.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <h2 className="text-xl font-semibold mb-4">My Markets</h2>
        <p className="text-sm text-muted-foreground mb-6">
          Select the states you work in. This helps us show you relevant properties.
        </p>
        
        <div className="space-y-6">
          {/* Current Markets */}
          <div>
            <h3 className="font-medium mb-3">Current States</h3>
            
            {userMarkets.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {userMarkets.map(market => (
                  <Badge key={market.id} className="bg-card border-border text-foreground flex items-center gap-2 pl-3 pr-2 py-2">
                    {market.name}
                    <button
                      type="button"
                      onClick={() => removeMarket(market.id)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                You haven't selected any states yet.
              </p>
            )}
          </div>
          
          {/* Add Markets */}
          <div>
            <h3 className="font-medium mb-3">Add States</h3>
            <div className="space-y-3">
              <Input
                placeholder="Search states..."
                value={marketSearch}
                onChange={(e) => setMarketSearch(e.target.value)}
                className="bg-muted"
              />
              
              <div className="max-h-[200px] overflow-y-auto border border-border rounded-md">
                {filteredStates.length > 0 ? (
                  filteredStates.map(state => (
                    <div
                      key={state.id}
                      className="py-2 px-3 hover:bg-muted cursor-pointer flex justify-between items-center"
                      onClick={() => addMarket(state)}
                    >
                      <span>{state.name}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                    </div>
                  ))
                ) : (
                  <div className="py-2 px-3 text-muted-foreground text-sm">
                    {marketSearch ? "No states found" : "Type to search for states"}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Buyer Display Preferences */}
        <div className="mt-8 pt-6 border-t border-border">
          <h3 className="font-medium mb-3">Buyer Display Preferences</h3>
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <Label htmlFor="hide-gross-margin" className="text-sm font-medium">
                Hide gross margin on my listings
              </Label>
              <p className="text-xs text-muted-foreground">
                When on, buyers and visitors won't see the calculated profit margin on any of your properties. You'll still see it yourself.
              </p>
            </div>
            <Switch
              id="hide-gross-margin"
              checked={hideGrossMargin}
              onCheckedChange={setHideGrossMargin}
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button
            type="button"
            className="bg-godyspo-gold hover:bg-godyspo-gold/90 text-black"
            onClick={handleSaveMarkets}
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default MarketsTab;
